import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of fog puffs, default 6 */
  count?: number;
  /** Fog colour (rgba), default soft white */
  color?: string;
  /** Base rise duration seconds, default 6 */
  dur?: number;
}

/**
 * SteamFog — soft blurred ellipses rising from the bottom and dissipating.
 */
const SteamFog: React.FC<Props> = ({
  count = 6,
  color = "rgba(255,255,255,0.28)",
  dur = 6,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: 10 + r(0) * 80,
      size: 60 + r(1) * 80,
      delay: r(2) * dur,
      d: dur * (0.8 + r(3) * 0.5),
      drift: (r(4) - 0.5) * 30,
    }),
    [count, dur]
  );

  return (
    <div style={overlayBase}>
      {parts.map((p) => {
        // Each puff gets its own unique keyframe so the drift value is
        // baked in — CSS custom props set via inline style can't be read
        // inside @keyframes reliably across browsers.
        const kf = `oot-fog-${p.id}`;
        return (
          <React.Fragment key={p.id}>
            <style>{`
              @keyframes ${kf} {
                0%   { transform: translate(0, 20px) scale(0.7); opacity: 0; }
                30%  { opacity: 0.6; }
                100% { transform: translate(${p.drift}px, -60px) scale(1.4); opacity: 0; }
              }
            `}</style>
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: `${p.left}%`,
                width: p.size,
                height: p.size * 0.6,
                borderRadius: "50%",
                background: color,
                filter: "blur(10px)",
                animation: `${kf} ${p.d}s ease-out ${p.delay}s infinite`,
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SteamFog;
